import { useState } from "react";
import CreationToolbar from "./CreationToolbar";
import FeatureTreeView from "./FeatureTreeView";
import FeaturePropertyPanel from "./FeaturePropertyPanel";
import SketchEditor from "./SketchEditor";
import type { Feature, PrimitiveFeature, BooleanFeature, ModifierFeature, ExtrudeFeature, RevolveFeature, TransformFeature, SketchFeature, SketchCommand, MateConstraint } from "./featureTypes";

interface DesignPanelProps {
  features: Feature[];
  selectedId: string | null;
  selectedFeature: Feature | null;
  onAdd: (type: PrimitiveFeature["type"]) => void;
  onAddExtrude: (profile: ExtrudeFeature["profile"]) => void;
  onAddSketch: () => void;
  onAddLoft: () => void;
  onAddRevolve: (profile: RevolveFeature["profile"]) => void;
  onAddSweep: () => void;
  onAddBoolean: (type: BooleanFeature["type"]) => void;
  onAddModifier: (type: ModifierFeature["type"]) => void;
  onAddTransform: (type: TransformFeature["type"]) => void;
  onAddImportedPart?: () => void;
  onStartMate?: () => void;
  onAddAssemblyPattern?: (type: "assemblyLinearPattern" | "assemblyCircularPattern") => void;
  onCheckInterference?: () => void;
  onAddSheetMetalBase?: () => void;
  onAddSheetMetalFlange?: () => void;
  onAddSheetMetalCornerRelief?: () => void;
  mateMode?: boolean;
  isAssembly?: boolean;
  onSelect: (id: string | null) => void;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, direction: "up" | "down") => void;
  onUpdate: (id: string, changes: Partial<Feature>) => void;
  onRemoveMate?: (featureId: string, mateId: string) => void;
  onUpdateMate?: (featureId: string, mateId: string, changes: Partial<MateConstraint>) => void;
  onUpdateSketchCommands: (id: string, commands: SketchCommand[], closed: boolean) => void;
}

export default function DesignPanel({
  features, selectedId, selectedFeature,
  onAdd, onAddExtrude, onAddSketch, onAddLoft, onAddRevolve, onAddSweep, onAddBoolean, onAddModifier, onAddTransform, onAddImportedPart, onStartMate, mateMode, isAssembly,
  onSelect, onToggle, onDelete, onMove, onUpdate, onRemoveMate, onUpdateMate, onAddAssemblyPattern, onCheckInterference, onAddSheetMetalBase, onAddSheetMetalFlange, onAddSheetMetalCornerRelief, onUpdateSketchCommands,
}: DesignPanelProps) {
  const [editingSketchId, setEditingSketchId] = useState<string | null>(null);

  // Sketch editing mode
  const editingSketch = editingSketchId ? features.find((f) => f.id === editingSketchId && f.type === "sketch") as SketchFeature | undefined : undefined;
  if (editingSketch) {
    return (
      <div className="design-panel">
        <SketchEditor
          feature={editingSketch}
          onUpdateCommands={(cmds, closed) => onUpdateSketchCommands(editingSketchId!, cmds, closed)}
          onExit={() => setEditingSketchId(null)}
        />
      </div>
    );
  }

  // Normal mode
  const booleanDisabled = features.length < 2;
  const modifierDisabled = features.length < 1;
  const transformDisabled = features.length < 1;
  return (
    <div className="design-panel">
      <CreationToolbar
        onAdd={onAdd} onAddExtrude={onAddExtrude} onAddSketch={onAddSketch} onAddLoft={onAddLoft} onAddRevolve={onAddRevolve} onAddSweep={onAddSweep}
        onAddBoolean={onAddBoolean} onAddModifier={onAddModifier} onAddTransform={onAddTransform}
        onAddImportedPart={onAddImportedPart} onStartMate={onStartMate} onAddAssemblyPattern={onAddAssemblyPattern} onCheckInterference={onCheckInterference}
        onAddSheetMetalBase={onAddSheetMetalBase} onAddSheetMetalFlange={onAddSheetMetalFlange} onAddSheetMetalCornerRelief={onAddSheetMetalCornerRelief}
        mateMode={mateMode} isAssembly={isAssembly}
        booleanDisabled={booleanDisabled} modifierDisabled={modifierDisabled} transformDisabled={transformDisabled}
        assemblyPatternDisabled={!features.some((f) => f.type === "importedPart")}
        sheetMetalFlangeDisabled={!features.some((f) => f.type === "sheetMetalBase" || f.type === "sheetMetalFlange")}
      />
      <FeatureTreeView
        features={features}
        selectedId={selectedId}
        onSelect={onSelect}
        onToggle={onToggle}
        onDelete={onDelete}
        onMove={onMove}
      />
      {selectedFeature && selectedFeature.type === "sketch" && (
        <div className="sketch-edit-section">
          <button className="sketch-edit-btn" onClick={() => setEditingSketchId(selectedFeature.id)} title="Open sketch editor to draw the profile">
            Edit Sketch
          </button>
          <FeaturePropertyPanel feature={selectedFeature} features={features} onUpdate={onUpdate} onRemoveMate={onRemoveMate} onUpdateMate={onUpdateMate} />
        </div>
      )}
      {selectedFeature && selectedFeature.type !== "sketch" && (
        <FeaturePropertyPanel feature={selectedFeature} features={features} onUpdate={onUpdate} onRemoveMate={onRemoveMate} onUpdateMate={onUpdateMate} />
      )}
    </div>
  );
}
